import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Download, Maximize2, Trash2, ImageIcon } from 'lucide-react';
import type { IPreviewGridProps, IAIStudioSession } from '@/@types/ai-studio';
import type { Id } from '../../../convex/_generated/dataModel';

interface IPreviewCellProps {
  session?: IAIStudioSession;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: () => void;
  onDiscard: () => void;
  onPreview: () => void;
  onDownload: () => void;
}

function PreviewCell({
  session,
  isSelected,
  isLoading,
  onSelect,
  onDiscard,
  onPreview,
  onDownload,
}: IPreviewCellProps) {
  // Common cell styles - fixed height with 9:16 aspect ratio
  const cellClass = "relative h-[500px] w-[281px] overflow-hidden rounded-xl";

  if (isLoading) {
    return (
      <div className={cn(cellClass, "border border-border bg-card")}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-xs text-muted-foreground">Generating...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!session || !session.imageUrl) {
    return (
      <div className={cn(cellClass, "border border-border bg-card")}>
        <div className="flex h-full items-center justify-center">
          <ImageIcon className="size-10 text-muted-foreground/30" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        cellClass,
        'group border-2 transition-all',
        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-border-hover'
      )}
    >
      {/* Image */}
      <img
        src={session.imageUrl}
        alt={session.params.sceneName}
        className="h-full w-full object-cover"
      />

      {/* Selection Checkbox */}
      <div className="absolute left-2 top-2 z-10">
        <div
          className={cn(
            'flex size-6 items-center justify-center rounded-md transition-all',
            isSelected ? 'bg-primary' : 'bg-background/80 backdrop-blur-sm'
          )}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelect}
            className={cn(
              'border-2',
              isSelected ? 'border-primary-foreground' : 'border-muted-foreground'
            )}
          />
        </div>
      </div>

      {/* Status Badge */}
      <div className="absolute right-2 top-2 z-10">
        <span className="rounded-md bg-primary/90 px-2 py-0.5 text-xs font-medium text-primary-foreground">
          Generated
        </span>
      </div>

      {/* Hover Actions */}
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onPreview}
          className="size-8 bg-background/80 text-foreground hover:bg-background"
          title="Preview"
        >
          <Maximize2 className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onDownload}
          className="size-8 bg-background/80 text-foreground hover:bg-background"
          title="Download"
        >
          <Download className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onDiscard}
          className="size-8 bg-destructive/80 text-white hover:bg-destructive"
          title="Discard"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      {/* Scene Name Overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
        <p className="truncate text-xs font-medium text-white">{session.params.sceneName}</p>
      </div>
    </div>
  );
}

export function PreviewGrid({
  sessions,
  isGenerating,
  batchSize,
  onSelectSession,
  onDiscardSession,
  onDownloadSession,
  onPreviewSession,
  selectedSessionIds,
}: IPreviewGridProps) {
  // Create array of 3 cells to match the design
  // Sessions are displayed in order, filling slots from left to right
  const gridCells = Array.from({ length: 3 }, (_, index) => {
    const session = sessions[index];
    const isWithinBatch = index < batchSize;
    return {
      index,
      session,
      isLoading: isGenerating && !session && isWithinBatch,
    };
  });

  const handleSelectSession = (sessionId: Id<'aiStudioSessions'>) => {
    onSelectSession(sessionId);
  };

  return (
    <div className="flex gap-4 h-full items-center justify-center">
      {gridCells.map((cell) => (
        <PreviewCell
          key={cell.index}
          session={cell.session}
          isSelected={cell.session ? selectedSessionIds.includes(cell.session._id) : false}
          isLoading={cell.isLoading}
          onSelect={() => cell.session && handleSelectSession(cell.session._id)}
          onDiscard={() => cell.session && onDiscardSession(cell.session._id)}
          onPreview={() => cell.session && onPreviewSession(cell.session._id)}
          onDownload={() => cell.session && onDownloadSession(cell.session._id)}
        />
      ))}
    </div>
  );
}
